import {
    ArrowDown,
    Box,
    Caption,
    Checkmark,
    Divider,
    DropdownContent,
    DropdownMenu,
    DropdownTrigger,
    ErrorIcon,
    IconButton,
    LinkText,
    Loader,
    TextBlock,
    Warning,
} from "@bcc-ui/ui-components";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
    DIVIDER_DOT,
    getJobStatusBackgroundColor,
    getJobStatusIcon,
    jobStatusTriggerText,
} from "./importJobStatusUtils";
import style from "./job-status-badge.scss";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { AssetImportJob, AssetImportJobStatus } from "services/asset-management/AssetImportJobService";
import { resolveColor } from "utils/commonFunctions";

interface JobsStatusBadgeProps {
    jobs: AssetImportJob[];
}

const getAggregateStatus = (jobs: AssetImportJob[]): AssetImportJobStatus => {
    if (jobs.some((job) => job.status === AssetImportJobStatus.STARTED)) {
        return AssetImportJobStatus.STARTED;
    }

    if (jobs.every((job) => job.status === AssetImportJobStatus.SUCCEEDED)) {
        return AssetImportJobStatus.SUCCEEDED;
    }

    if (jobs.every((job) => job.status === AssetImportJobStatus.FAILED)) {
        return AssetImportJobStatus.FAILED;
    }

    return AssetImportJobStatus.WARNING;
};

const getJobRowIcon = (status: AssetImportJobStatus): ReactNode => {
    switch (status) {
        case AssetImportJobStatus.SUCCEEDED:
            return <Checkmark size={20} color={resolveColor("--theme-success")} />;

        case AssetImportJobStatus.FAILED:
            return <ErrorIcon size={20} />;

        case AssetImportJobStatus.WARNING:
            return <Warning size={20} />;

        case AssetImportJobStatus.STARTED:
            return <Loader small />;
    }
};

export const JobsStatusBadge = (props: JobsStatusBadgeProps) => {
    const { t } = useTranslation();

    const amountOfFailedJobs = props.jobs.filter((job) => job.status === AssetImportJobStatus.FAILED).length;
    const amountOfQueuedJobs = props.jobs.filter((job) => job.status === AssetImportJobStatus.WARNING).length;
    const amountOfSucceededJobs = props.jobs.filter((job) => job.status === AssetImportJobStatus.SUCCEEDED).length;
    const amountOfStartedJobs = props.jobs.filter((job) => job.status === AssetImportJobStatus.STARTED).length;
    const hasMixedStatuses = props.jobs.some((job) => job.status !== props.jobs[0].status);

    const jobsWithStatusesArray = [
        { status: AssetImportJobStatus.FAILED, count: amountOfFailedJobs },
        { status: AssetImportJobStatus.WARNING, count: amountOfQueuedJobs },
        { status: AssetImportJobStatus.SUCCEEDED, count: amountOfSucceededJobs },
    ];

    const getTriggerBadgeIcons = () =>
        jobsWithStatusesArray
            .filter((statusCount) => statusCount.count > 0)
            .map((statusCount, index) => (
                <React.Fragment key={statusCount.status}>
                    {index > 0 && <Divider direction={"Vertical"} className={style.dividerHeight} />}
                    {getJobStatusIcon(statusCount.status)} ({statusCount.count})
                </React.Fragment>
            ));

    const getJobAssetsText = (job: AssetImportJob): ReactNode => {
        const supportLink = (
            <LinkText as={"a"} href={`mailto:${SUPPORT_EMAIL}`}>
                {t("Onboarding.home.contactSupport")}
            </LinkText>
        );

        switch (job.status) {
            case AssetImportJobStatus.STARTED:
                return (
                    <>
                        {t("AssetManagement.overview.totalAssetsCard.jobAssetsProgress", {
                            added: job.totalImportedAssets,
                            count: job.totalRequestedAssets,
                        })}
                    </>
                );

            case AssetImportJobStatus.WARNING:
                return (
                    <Box columnLayout gap={"Xxxs"}>
                        <Caption className={style.warningTextColor}>
                            {t("AssetManagement.overview.totalAssetsCard.assetsAddedTotal", {
                                count: job.totalImportedAssets,
                            })}
                            {DIVIDER_DOT}
                            {t("AssetManagement.overview.totalAssetsCard.assetsQueued", {
                                count: job.totalFailedAssets,
                            })}
                        </Caption>
                        {supportLink}
                    </Box>
                );

            case AssetImportJobStatus.FAILED:
                return (
                    <Box columnLayout gap={"Xxxs"}>
                        <Caption errorStyle>
                            {t("Common.state.failed")}
                            {DIVIDER_DOT}
                            {t("AssetManagement.overview.totalAssetsCard.jobAssetsProgress", {
                                added: 0,
                                count: job.totalRequestedAssets,
                            })}
                        </Caption>
                        {supportLink}
                    </Box>
                );

            case AssetImportJobStatus.SUCCEEDED:
                return t("AssetManagement.overview.totalAssetsCard.jobAssetsProgress", {
                    added: job.totalImportedAssets,
                    count: job.totalRequestedAssets,
                });
        }
    };

    const aggregateStatus = getAggregateStatus(props.jobs);

    const badgeLabel = (() => {
        switch (aggregateStatus) {
            case AssetImportJobStatus.STARTED:
                return t("AssetManagement.overview.totalAssetsCard.importing", {
                    count: amountOfStartedJobs,
                });

            case AssetImportJobStatus.FAILED:
                return t("AssetManagement.overview.totalAssetsCard.import_failed");

            case AssetImportJobStatus.SUCCEEDED:
            case AssetImportJobStatus.WARNING:
                return t("AssetManagement.overview.totalAssetsCard.import_complete");
        }
    })();

    return (
        <DropdownMenu position="bottom-start" dropdownOffset={"Xxs"}>
            <DropdownTrigger dataType="ASSETS_IMPORTING">
                <Box
                    verticalAlignment={"Center"}
                    horizontalAlignment={"Center"}
                    gap={"Xxxs"}
                    className={style.badgeBase}
                    style={{
                        backgroundColor: !hasMixedStatuses ? getJobStatusBackgroundColor(aggregateStatus) : undefined,
                    }}
                >
                    {amountOfStartedJobs > 0 && (
                        <>
                            <Loader small />
                            <TextBlock disableBottomSpacing>{badgeLabel}</TextBlock>
                            <Divider direction={"Vertical"} className={style.dividerHeight} />
                        </>
                    )}
                    {amountOfStartedJobs === 0 && hasMixedStatuses && (
                        <>
                            <TextBlock disableBottomSpacing>
                                {t("AssetManagement.overview.totalAssetsCard.jobAssetsFinished", {
                                    count: props.jobs.length,
                                })}
                            </TextBlock>
                            <Divider direction={"Vertical"} className={style.dividerHeight} />
                        </>
                    )}
                    {amountOfStartedJobs === 0 && !hasMixedStatuses && (
                        <>
                            {getJobStatusIcon(props.jobs.at(props.jobs.length - 1)!.status)}
                            {jobStatusTriggerText(props.jobs.at(props.jobs.length - 1)!, props.jobs.length)}
                        </>
                    )}
                    {hasMixedStatuses && (
                        <Box gap={"Xxs"} verticalAlignment={"Center"}>
                            {getTriggerBadgeIcons()}
                        </Box>
                    )}
                    <IconButton noHoverStyle>
                        <ArrowDown />
                    </IconButton>
                </Box>
            </DropdownTrigger>
            <DropdownContent className={style.triggerBadgeInfoList}>
                {props.jobs.map((job, index) => (
                    <Box
                        key={index}
                        justifyContent={"SpaceBetween"}
                        verticalAlignment={"Center"}
                        className={style.jobRow}
                    >
                        <Box columnLayout gap={"Xxxs"}>
                            <TextBlock disableBottomSpacing>
                                {/* BCC-6890: change jobName? */}
                                {t("AssetManagement.overview.totalAssetsCard.jobName", { index: index + 1 })}
                            </TextBlock>
                            {getJobAssetsText(job)}
                        </Box>
                        {getJobRowIcon(job.status)}
                    </Box>
                ))}
            </DropdownContent>
        </DropdownMenu>
    );
};
